import { MoreHorizontal20Regular } from '@fluentui/react-icons';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/Dropdown';
import React, { useState } from 'react';
import CreateTemplateFromCurrentItem from './CreateTemplateFromCurrentItem';
import ChangeTypemarkStateMenuItem from '../ChangeTypemarkStateButton';
import { useMutation, useQueryClient } from 'react-query';
import { apiService } from '../../../../../utils/api';
import { getApiErrorMessage } from '../../../../../utils/apiError';
import { toast } from 'sonner';
import TemplateModal from '../modals/TemplateModal';
import { useTypemarkContext } from '../../TypemarkContext';

type ActionsDropdownProps = {
  selectedDetail: Record<string, any>;
};

type ApplyContentMethodType = 'REPLACE' | 'MERGE_DIFF_KEEP' | 'MERGE_DIFF_UPDATE';
type ApplyContentSourceType = 'TEMPLATE' | 'TYPEMARK';

const ActionsDropdown = ({ selectedDetail }: ActionsDropdownProps) => {
  const { selected, setCopiedItem, copiedItem } = useTypemarkContext() || {};
  const queryClient = useQueryClient();

  const selectedIds = Object.values(selected);
  const isMultiMode = selectedIds.length > 1;

  const [method, setMethod] = useState<ApplyContentMethodType>('REPLACE');
  const [templateModalOpen, setTemplateModalOpen] = useState(false);

  const applyContent = useMutation({
    mutationFn: ({
      id,
      sourceType,
      method,
    }: {
      id: string;
      sourceType: ApplyContentSourceType;
      method?: ApplyContentMethodType;
    }) => {
      return apiService.post(`/api/v1/instance-type/content/apply/`, {
        targets: selectedIds,
        mode: method,
        source: {
          type: sourceType,
          id: id,
        },
      });
    },
    onSuccess: () => {
      toast.success('Akce byla úspěšně provedena');
      setMethod('REPLACE');
      queryClient.invalidateQueries({ queryKey: ['instance-type'] });
    },
    onError: (err) => {
      console.error(err);
      toast.error(getApiErrorMessage(err));
    },
  });

  const handleApplyTemplateContent = (method: ApplyContentMethodType) => {
    setMethod(method);
    setTemplateModalOpen(true);
  };

  const handleApplyTypemarkContent = (method: ApplyContentMethodType) => {
    applyContent.mutate({ id: copiedItem || selectedDetail?.id, sourceType: 'TYPEMARK', method });
  };

  const handlePickTemplate = (templateId: string) => {
    setTemplateModalOpen(false);
    applyContent.mutate({ id: templateId, sourceType: 'TEMPLATE', method });
  };

  const handleCopyContent = () => {
    setCopiedItem(selectedDetail?.id);

    toast.success('Obsah byl úspěšně zkopírován');
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button variant="outline" data-testid="typemark-actions-dropdown-btn">
            Možnosti akcí
            <MoreHorizontal20Regular />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent sideOffset={2}>
          <DropdownMenuGroup>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="typemark-actions-load-template-trigger">
                Nahrát obsah ze šablony
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem
                    data-testid="typemark-actions-template-replace"
                    onClick={() => handleApplyTemplateContent('REPLACE')}
                  >
                    Nahradit data novými
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="typemark-actions-template-merge-keep"
                    onClick={() => handleApplyTemplateContent('MERGE_DIFF_KEEP')}
                  >
                    Přidat pouze rozdílná, shodná ponechat
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="typemark-actions-template-merge-update"
                    onClick={() => handleApplyTemplateContent('MERGE_DIFF_UPDATE')}
                  >
                    Přidat pouze rozdílná, shodná aktualizovat (aktualizuje hodnoty u vlastností)
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            {!isMultiMode && <CreateTemplateFromCurrentItem id={selectedIds[0] as string} />}
            {!isMultiMode && (
              <DropdownMenuItem
                data-testid="typemark-actions-copy-content"
                onClick={() => handleCopyContent()}
              >
                Kopírovat obsah
              </DropdownMenuItem>
            )}

            {copiedItem && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger data-testid="typemark-actions-paste-content-trigger">
                  Vložit obsah
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem
                      data-testid="typemark-actions-paste-replace"
                      onClick={() => handleApplyTypemarkContent('REPLACE')}
                    >
                      Nahradit data novými
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="typemark-actions-paste-merge-keep"
                      onClick={() => handleApplyTypemarkContent('MERGE_DIFF_KEEP')}
                    >
                      Přidat pouze rozdílná, shodná ponechat
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="typemark-actions-paste-merge-update"
                      onClick={() => handleApplyTypemarkContent('MERGE_DIFF_UPDATE')}
                    >
                      Přidat pouze rozdílná, shodná aktualizovat (aktualizuje hodnoty u vlastností)
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )}
          </DropdownMenuGroup>

          {!isMultiMode && selectedDetail && (
            <>
              <DropdownMenuSeparator />
              <ChangeTypemarkStateMenuItem
                isActive={selectedDetail?.is_active}
                id={selectedDetail?.id}
              />
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <TemplateModal
        title="Nahrát obsah ze šablony"
        visible={templateModalOpen}
        classificationId={selectedDetail?.classification?.id}
        onClose={() => setTemplateModalOpen(false)}
        onSubmit={handlePickTemplate}
      />
    </>
  );
};

export default ActionsDropdown;
